import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ComponentDefinition } from '@/pages/reports/schemas';
import { FieldFactory } from '@/pages/reports/components/form';
import type { ComponentField } from '@/types/reports';

interface ComponentConfigFormProps {
  component: ComponentDefinition;
  componentProps: Record<string, any>;
  validationErrors: Record<string, string>;
  onPropChange: (_key: string, _value: string | any[]) => void;
  onSave: () => void;
  onCancel: () => void;
  saveButtonText?: string;
}

type FieldSegment = { group?: string; fields: ComponentField[] };

function buildSegments(fields: ComponentField[]): FieldSegment[] {
  const segments: FieldSegment[] = [];
  let current: FieldSegment = { group: undefined, fields: [] };

  fields.forEach((field) => {
    const group = 'group' in field ? field.group : undefined;
    if (group !== current.group) {
      if (current.fields.length > 0) {
        segments.push(current);
      }
      current = { group, fields: [field] };
    } else {
      current.fields.push(field);
    }
  });
  if (current.fields.length > 0) segments.push(current);
  return segments;
}

export const ComponentConfigForm: React.FC<ComponentConfigFormProps> = ({
  component,
  componentProps,
  validationErrors,
  onPropChange,
  onSave,
  onCancel,
  saveButtonText = 'Adicionar'
}) => {
  const segments = useMemo(() => buildSegments(component.fields), [component.fields]);

  const buildFieldConfig = (field: ComponentField) => ({
    key: field.key,
    label: field.label,
    type: field.type as 'text' | 'textarea' | 'rich-text' | 'url' | 'url-text' | 'links' | 'image' | 'logo-links' | 'hashtag-categories' | 'creator-entries' | 'brand-entries' | 'slides' | 'highlights' | 'social' | 'bullets' | 'select' | 'tooltip-list' | 'news-subsections' | 'thematic-subsections' | 'curatorship-items' | 'date',
    required: field.required,
    placeholder: field.type === 'links'
      ? `Adicionar ${field.label.toLowerCase()}`
      : field.type === 'select'
        ? 'Selecione...'
        : `Digite ${field.label.toLowerCase()}`,
    rows: field.type === 'textarea' ? 3 : undefined,
    ...('options' in field && { options: field.options }),
    ...('group' in field && { group: field.group }),
  });

  const isUrlGroup = (fields: ComponentField[]) =>
    fields.length > 1 && fields.every((f) => f.type === 'url' || f.type === 'image');

  return (
    <div className="space-y-4">
      {segments.map((segment, segmentIndex) => (
        <div key={segment.group ?? `segment-${segmentIndex}`}>
          {segment.group && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {segment.group}
              </h3>
              {isUrlGroup(segment.fields) ? (
                <div className="grid grid-cols-2 gap-3">
                  {segment.fields.map((field) => (
                    <div key={field.key}>
                      <FieldFactory
                        field={buildFieldConfig(field)}
                        value={componentProps[field.key]}
                        onChange={(value: string | any[]) => onPropChange(field.key, value)}
                        error={validationErrors[field.key]}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4 pl-0">
                  {segment.fields.map((field) => (
                    <div key={field.key}>
                      <FieldFactory
                        field={buildFieldConfig(field)}
                        value={componentProps[field.key]}
                        onChange={(value: string | any[]) => onPropChange(field.key, value)}
                        error={validationErrors[field.key]}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!segment.group && (
            <div className="space-y-4">
              {segment.fields.map((field) => (
                <div key={field.key}>
                  <FieldFactory
                    field={buildFieldConfig(field)}
                    value={componentProps[field.key]}
                    onChange={(value: string | any[]) => onPropChange(field.key, value)}
                    error={validationErrors[field.key]}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-4">
        <Button variant="dark" onClick={onSave}>
          {saveButtonText}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};
